import React from 'react';
import { Button, ButtonGroup, Tooltip } from '@mui/material';
import { useAccessControl } from 'new/state/app/repository/useAppRepository';
import { useAppAdapter } from 'new/state/app/adapter/useAppAdapter';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';

import { ReactComponent as CheckIcon } from 'new/components/icons/check-filled.svg';
import { ReactComponent as UserIcon } from 'new/components/icons/fi-rr-user.svg';
import { ReactComponent as GroupIcon } from 'new/components/icons/fi-rr-users.svg';

import s from './AccessLevelControl.module.scss';

export type AccessLevelControlProps = {
  /**
   * If true, the parent component will control the access level control
   */
  controlled?: boolean;
  isOwner?: boolean;
  aclQueryMode?: string;
  isCollaborator?: boolean;
  onChangeAccessLevel?: (level: string) => void;
  /**
   * Called whenever the access level changes from this control (after the primary handler runs).
   */
  onChangeAccessLevelListening?: (level: string) => void;
};

const useAccessLevelControl = (props: AccessLevelControlProps) => {
  const { isOwner } = useAccessControl();
  const { setAccessControl: onChangeAccessLevel } = useAppAdapter();

  const memoizedOnChangeAccessLevel = React.useMemo(
    () => (props.controlled ? props?.onChangeAccessLevel : onChangeAccessLevel),
    [props.controlled],
  );

  if (props.controlled) {
    return {
      isOwner: props?.isOwner,
      onChangeAccessLevel: memoizedOnChangeAccessLevel,
    };
  }

  return {
    isOwner,
    onChangeAccessLevel: memoizedOnChangeAccessLevel,
  };
};

const AccessLevelControl = (props: AccessLevelControlProps) => {
  const { isOwner, onChangeAccessLevel } = useAccessLevelControl(props);
  const { onChangeAccessLevelListening } = props;

  const handleChangeAccessLevel = React.useCallback(
    (selectedAccessLevel: string) => {
      onChangeAccessLevel(selectedAccessLevel);
      onChangeAccessLevelListening?.(selectedAccessLevel);
    },
    [onChangeAccessLevel, onChangeAccessLevelListening],
  );

  return (
    <ButtonGroup
      variant="outlined"
      className={s.container}
      aria-label={`Access Level-${props.controlled ? 'controlled' : 'uncontrolled'}`}
    >
      <Tooltip title="Owned by me">
        <Button
          variant={isOwner ? 'ghost' : 'outlined'}
          onClick={() => handleChangeAccessLevel(ACLQueryModeTypes.MODE_OWNER.key)}
        >
          <UserIcon />
          {isOwner && <CheckIcon width={16} height={16} />}
        </Button>
      </Tooltip>

      <Tooltip title="Accessible to me">
        <Button
          variant={!isOwner ? 'ghost' : 'outlined'}
          onClick={() => handleChangeAccessLevel(ACLQueryModeTypes.MODE_COLLABORATOR.key)}
        >
          {!isOwner && <CheckIcon fill="parent" width={16} height={16} />}
          <GroupIcon />
        </Button>
      </Tooltip>
    </ButtonGroup>
  );
};

AccessLevelControl.displayName = 'AccessLevelControl';

export { AccessLevelControl };
